import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BaseDataService, HttpResponsesHandlersFactory } from "@bbwt/modules/data-service";
import { EpisodeService } from "@project/episode/episode.service";
import { IEpisode } from "@project/models/episodes/episode";
import { BehaviorSubject, combineLatestWith, map } from "rxjs";
import { MoveSubentityType } from "./subentity-type";

@Injectable({
    providedIn: "root"
})
export class ConsultationMergeMoveService extends BaseDataService {
    url: string;
    // Move subentity fields
    private _nullSubentity = { id: <string>null, type: <MoveSubentityType>null };
    private _movingSubEntities: { [k in MoveSubentityType]: string[] };
    private _subEntityPreselected = false;

    private _movingEpisodeId = new BehaviorSubject<string>(null);
    private _firstSubentityId = new BehaviorSubject(this._nullSubentity);
    private _isMoveModeOn = new BehaviorSubject<boolean>(false);
    private _subentityLength = new BehaviorSubject<number>(0);

    // Merge episode fields
    private _mergeEpisodes: IEpisode[] = [];
    private _mergeEpisodesMap: { [k: string]: IEpisode } = {};

    private _isMergeModeOn = new BehaviorSubject<boolean>(false);
    private _mergeEpisodeLength = new BehaviorSubject<number>(0);
    private _firstMergeEpisodeId = new BehaviorSubject<string>(null);

    constructor(http: HttpClient, responseHandlerFactory: HttpResponsesHandlersFactory, private episodeService: EpisodeService) {
        super(http, responseHandlerFactory);
        this.initValues();
    }

    // Move subentity getters
    get movingFromEpisodeId$() {
        return this._movingEpisodeId.asObservable();
    }

    get firstSubentityId$() {
        return this._firstSubentityId.asObservable();
    }

    get isMoveModeOn$() {
        return this._isMoveModeOn.asObservable();
    }

    get subentityLength$() {
        return this._subentityLength.asObservable();
    }

    // Merge episode getters
    get mergeEpisodes() {
        return [...this._mergeEpisodes];
    }

    get isMergeModeOn$() {
        return this._isMergeModeOn.asObservable();
    }

    get mergeEpisodeLength$() {
        return this._mergeEpisodeLength.asObservable();
    }

    get firstMergeEpisodeId$() {
        return this._firstMergeEpisodeId.asObservable();
    }

    get isMergeMoveModeOn$() {
        return this.isMoveModeOn$.pipe(
            combineLatestWith(this.isMergeModeOn$),
            map(([isMoveModeOn, isMergeModeOn]) => isMoveModeOn || isMergeModeOn)
        );
    }

    startMoveSubentityFrom(episodeId: string) {
        if (this.startOperationInvalid(episodeId)) {
            return;
        }

        this.initValues();

        this._movingEpisodeId.next(episodeId);
        this._isMoveModeOn.next(true);
    }

    selectSubentity(id: string, type: MoveSubentityType) {
        if (this.subentityOperationInvalid(id, type)) {
            return;
        }

        if (!this._subEntityPreselected) {
            this._subEntityPreselected = true;
            this._firstSubentityId.next({ id, type });
            return;
        }

        this._movingSubEntities[type].includes(id) || this._movingSubEntities[type].push(id);
        this.emitSubentityLength();
    }

    removeSubentity(id: string, type: MoveSubentityType) {
        if (this.subentityOperationInvalid(id, type)) {
            return;
        }

        this._movingSubEntities[type] = this._movingSubEntities[type].filter(i => i !== id);
        this.emitSubentityLength();
    }

    startMergeEpisode(episode: IEpisode) {
        if (this.startOperationInvalid(episode?.id)) {
            return;
        }

        this.initValues();

        this._isMergeModeOn.next(true);

        this._mergeEpisodes.push(episode);
        this._mergeEpisodesMap[episode.id] = episode;
        this.emitMergeEpisodeLength();
        this._firstMergeEpisodeId.next(episode.id);

    }

    selectEpisode(episode: IEpisode) {
        if (this.mergeOperationInvalid(episode)) {
            return;
        }

        if (!(episode.id in this._mergeEpisodesMap)) {
            this._mergeEpisodes.push(episode);
            this._mergeEpisodesMap[episode.id] = episode;
            this.emitMergeEpisodeLength();
        }
    }

    removeEpisode(episode: IEpisode) {
        if (this.mergeOperationInvalid(episode)) {
            return;
        }

        if (episode.id in this._mergeEpisodesMap) {
            this._mergeEpisodes = this._mergeEpisodes.filter(e => e.id !== episode.id);
            delete this._mergeEpisodesMap[episode.id];
            this.emitMergeEpisodeLength();
        }
    }

    hasEpisodeSelected(id: string): boolean {
        return id in this._mergeEpisodesMap;
    }

    cancel() {
        if (!this._isMoveModeOn.value && !this._isMergeModeOn.value) {
            return;
        }

        this.initValues();

        this.emitMoveSubentityDefaults();
        this.emitMergeEpisodeDefaults();
    }

    async moveSubentity(targetEpisodeId: string) {
        if (!this._isMoveModeOn.value || !targetEpisodeId) {
            return;
        }

        try {
            const { Document, Note, Prescription } = this._movingSubEntities;
            await this.episodeService.moveEpisode(targetEpisodeId, this._movingEpisodeId.value, Document, Note, Prescription);
        } finally {
            this.cancel();
        }
    }

    async mergeEpisode(masterEpisodeId: string) {
        if (!this._isMergeModeOn.value || !masterEpisodeId) {
            return;
        }

        try {
            const episodeIds = this._mergeEpisodes.map(e => e.id);
            await this.episodeService.merge(masterEpisodeId, episodeIds);
        } finally {
            this.cancel();
        }
    }

    private startOperationInvalid(episodeId: string) {
        return this._isMoveModeOn.value || this._isMergeModeOn.value || !episodeId;
    }

    private subentityOperationInvalid(id: string, type: MoveSubentityType) {
        return !this._isMoveModeOn.value || !(type in this._movingSubEntities) || !id;
    }

    private mergeOperationInvalid(episode: IEpisode) {
        return !this._isMergeModeOn.value || !episode?.id || !this.episodeService.canMergeEpisode(episode);
    }

    private emitMoveSubentityDefaults() {
        if (this._isMoveModeOn) {
            this._movingEpisodeId.next(null);
            this._isMoveModeOn.next(false);
            this._firstSubentityId.next(this._nullSubentity);
            this.emitSubentityLength();
        }
    }

    private emitMergeEpisodeDefaults() {
        if (this._isMergeModeOn.value) {
            this._isMergeModeOn.next(false);
            this._firstMergeEpisodeId.next(null);
            this.emitMergeEpisodeLength();
        }
    }

    private emitSubentityLength() {
        const length = Object.keys(this._movingSubEntities).reduce((length, key) => length + this._movingSubEntities[key].length, 0);
        this._subentityLength.next(length);
    }

    private emitMergeEpisodeLength() {
        this._mergeEpisodeLength.next(this._mergeEpisodes.length);
    }

    private initValues() {
        this._movingSubEntities = {
            Document: [],
            Note: [],
            Prescription: []
        };
        this._subEntityPreselected = false;

        this._mergeEpisodes = [];
        this._mergeEpisodesMap = {};
    }
}
